// @flow
import classnames from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { makeStyles } from '@material-ui/styles';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';
import OpenInNewOutlinedIcon from '@material-ui/icons/OpenInNewOutlined';

import SignInSignOut from './SignInSignOut';
import ProfileDetails from './ProfileDetails';
import DropdownMenu from '../Dropdowns/DropdownMenu';
import ArrowDownIcon from '../Icons/ArrowDownIcon';
import UserIcon from '../Icons/UserIcon';

import { getDisplayName } from '../../utils/user';

const useStyles = makeStyles(theme => ({
  container: {
    position: 'relative',
  },
  button: {
    display: 'flex',
    alignItems: 'center',
    maxWidth: 238,
    paddingLeft: 0,
    paddingRight: 0,
    '&:hover': {
      backgroundColor: 'inherit',
    },
  },
  name: {
    flex: 1,
    marginLeft: 16,
    marginRight: 12,
    color: theme.palette.type === 'light' ? theme.palette.ava.dark1 : theme.palette.ava.white,
    fontSize: 20,
    lineHeight: '26px',
    textAlign: 'left',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  list: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  item: {
    paddingTop: 6,
    paddingRight: 24,
    paddingBottom: 6,
    paddingLeft: 24,
    fontSize: 18,
    lineHeight: '27px',
    '&:hover': {
      background: theme.palette.type === 'light' ? theme.palette.ava.navy5 : theme.palette.ava.grey5,
    },
  },
  link: {
    color: theme.palette.type === 'light' ? theme.palette.ava.dark1 : theme.palette.ava.white,
    '&:hover, &:focus': {
      color: theme.palette.type === 'light' ? theme.palette.ava.dark1 : theme.palette.ava.white,
      textDecoration: 'none',
    },
  },
  blue: {
    color: theme.palette.ava.blue2,
  },
}));

interface Props {
  user: Object;
}

const ProfileMenu = ({ user }: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const onClick = event => setAnchorEl(event.currentTarget);
  const onClose = () => setAnchorEl(null);

  if (!user) return null;

  return (
    <div className={classes.container}>
      <Button
        classes={{
          root: classes.button,
        }}
        id="profile-button"
        aria-controls="profile-menu"
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        disableElevation
        disableRipple
        onClick={onClick}
      >
        <UserIcon />
        <Typography className={classnames(classes.name, { [classes.blue]: open })} component="div">
          {getDisplayName(user)}
        </Typography>
        <ArrowDownIcon className={classnames({ [classes.blue]: open })} rotated={open} />
      </Button>
      <DropdownMenu
        id="profile-menu"
        anchorEl={anchorEl}
        width={325}
        onClose={onClose}
        anchorOrigin={{ horizontal: 'right' }}
        transformOrigin={{ horizontal: 'right' }}
      >
        <MenuItem disableGutters disabled style={{ opacity: 1 }}>
          <ProfileDetails />
        </MenuItem>
        <Divider />
        <MenuItem className={classes.item}>
          <Link className={classes.link} to="/web/account-settings/my-profile">
            {t('profileMenu.myProfile')}
          </Link>
        </MenuItem>
        <MenuItem className={classes.item}>
          <Link className={classes.link} to="/web/account-settings/general">
            {t('profileMenu.generalSettings')}
          </Link>
        </MenuItem>
        {user?.profile?.orgProperties?.role === 'admin' && (
          <MenuItem className={classes.item}>
            <a className={classes.link} target="_blank" rel="noreferrer noopener" href="/account/dashboard">
              {t('sidebar.adminDashboard')}
              <OpenInNewOutlinedIcon
                style={{
                  fontSize: '1em',
                  transform: 'translate(0.2em, 0.16em)',
                }}
              />
            </a>
          </MenuItem>
        )}
        <SignInSignOut />
      </DropdownMenu>
    </div>
  );
};

const mapStateToProps = ({ scribeSession: { captioner } }) => ({ user: captioner });

export default connect(mapStateToProps)(ProfileMenu);
